import React, { useCallback, useContext, useEffect } from 'react';
import { css } from '@emotion/css';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import BookingConfirmed from 'components/BookingConfirmed';
import { colors } from 'styles/colors';
import { BookingAPI } from 'utils/BookingAPI';
import { setBookingBusinessDetails } from 'store/bookingSlice';
import { AuthContext } from 'index';

import Logo from 'assets/icons/logo.svg';
import CheckCircle from 'assets/icons/check-circle.svg';
import VectorCross from 'assets/icons/vector-cross.svg';

const ConfirmedBookingsPage = () => {
    const dispatch = useDispatch();
    const auth = useContext(AuthContext);
    const user = useSelector((state: RootState) => state.user.user);
    const bookingsRecord = useSelector((state: RootState) => state.booking.bookings);
    const token = useSelector((state: RootState) => state.global.authToken);
    const bookingBusinessDetails = useSelector((state: RootState) => state.booking.BookingBusinessDetails);

    const getBookingBusinessDetailsFromAPI = useCallback(async () => {
        if (token) {
            const bookingBusinessDetailsResponse = await BookingAPI.getBookingBusinessDetails(token);
            dispatch(setBookingBusinessDetails(bookingBusinessDetailsResponse));
        }
    }, [dispatch, token]);

    const onLogout = useCallback(() => {
        localStorage.removeItem('id_token');
        if (process.env.NODE_ENV !== 'development') {
            localStorage.setItem('logoutCheck', Date.now().toString());
        }
        auth.logout({
            redirectUri: process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : 'https://planetdiet.dk/',
        });
    }, [auth]);

    useEffect(() => {
        getBookingBusinessDetailsFromAPI();
    }, [getBookingBusinessDetailsFromAPI, token]);

    if (!user) return null;

    const closeConfirmationPage = async () => {
        try {
            onLogout();
        } catch {}
    };

    const getTextDuration = (duration: string) => {
        var parts = duration.split(':');
        var hours = parseInt(parts[0]);
        var minutes = parseInt(parts[1]);
        var seconds = parseInt(parts[2]);

        var result = [];

        if (hours > 0) {
            result.push(hours + ' timer');
        }

        if (minutes > 0) {
            result.push(minutes + ' minutter');
        }

        if (seconds > 0) {
            result.push(seconds + ' sekunder');
        }

        return result.join(' ');
    };
    const bookings = (Object.values(bookingsRecord).filter(Boolean) as NonNullable<typeof bookingsRecord[keyof typeof bookingsRecord]>[]);

    return (
        <>
            <div className={styles.container}>
                <div className={styles.content}>
                    <img className={styles.vector} src={VectorCross} alt={'Vector'} onClick={closeConfirmationPage} />
                    <img className={styles.logo} src={Logo} alt={'Logo'} />
                    <div className={styles.thanksRow}>
                        <img className={styles.checkIcon} src={CheckCircle} alt="check" />
                        <span className={styles.thanksText}>
                            Tak, fordi du har reserveret hos os. Du modtager snart en bekræftelsesmail
                        </span>
                    </div>
                    {bookings.length > 0 && <span className={styles.namedText}>Kommende booking for {user?.name ?? 'you'}</span>}
                    {bookings.length === 0 && <span className={styles.namedText}>There are no upcoming bookings for you</span>}
                    {bookings.map((booking) => (
                        <BookingConfirmed
                            key={booking.dayId}
                            title={booking.title}
                            date={new Date(booking.date)}
                            duration={getTextDuration(booking.duration)}
                            location={booking.location}
                        />
                    ))}
                </div>
                <div className={styles.footer}>
                    <div className={styles.footerText}>
                        {bookingBusinessDetails?.displayAddress} |{' '}
                        <a href={bookingBusinessDetails?.webSiteUrl}>planetdiet.dk</a> <br></br> Politikker og praksis
                        for {bookingBusinessDetails?.displayName} gælder for brugen af dine data
                    </div>
                </div>
            </div>
        </>
    );
};

const styles = {
    container: css({
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        justifyContent: 'space-between',
        width: '100%',
    }),
    content: css({
        flex: '1',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '2rem',
        marginTop: '4.5rem',
    }),
    thanksText: css({
        textAlign: 'center',
        color: '#333',
        fontFamily: 'Segoe UI',
        fontSize: '0.9375rem',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 'normal',
    }),
    thanksRow: css({
        width: '18.6rem',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignContent: 'center',
        marginTop: '3.60rem',
    }),
    checkIcon: css({
        width: 20,
        height: 20,
        marginTop: '10px',
    }),
    namedText: css({
        color: colors.primaryText,
        marginTop: '2.5rem',
        marginBottom: '1.5rem',
        textAlign: 'center',
        fontFamily: 'Segoe UI',
        fontSize: '0.8125rem',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '0.9375rem',
    }),
    logo: css({
        width: '7.5rem',
        objectFit: 'contain',
    }),
    footer: css({
        width: '100%',
        background: '#F8F8F8',
        boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.10)',
        marginTop: 'auto',
        height: '15rem',
    }),
    footerText: css({
        width: '100%',
        maxWidth: '31rem',
        margin: '0 auto',
        color: '#605E5C',
        textAlign: 'center',
        fontFamily: 'Segoe UI',
        fontSize: '0.875rem',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '1.25rem',
        marginTop: '3.63rem',
        a: {
            textDecoration: 'underline',
            color: '#605E5C',
            textAlign: 'center',
            fontFamily: 'Segoe UI',
            fontSize: '0.875rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '1.25rem',
        },
    }),
    linkCustomStyle: css({
        color: '#605E5C !important',
        fontSize: '0.875rem !important',
        fontStyle: 'normal !important',
        fontWeight: '400 !important',
        lineHeight: '1.25rem !important',
    }),
    vector: css({
        width: '1.5rem',
        position: 'absolute',
        right: '1.56rem',
        top: '5.84rem',
        cursor: 'pointer',
    }),
};

export default ConfirmedBookingsPage;
